import ObjectModelEntry from '~/models/objectModelEntries/objectModelEntry'
import ListViewImageUpload from "~/models/listViews/listViewImageUpload";
import * as _ from "lodash";
import {Logger} from "~/services/logger";
import Dropzone from 'dropzone';
import accountRelatable from "~/mixins/objectModelEntries/accountRelatable";

export default {
  mixins: [accountRelatable],
  data: function() {
    return {
      uploadsToDelete: [],
    };
  },
  computed: {
    objectModelId(){
      return this.selectedObjectModel?.object_model_id ||  this.selectedObjectModelEntry?.published_object_model.object_model_id
    },
    url() {
      return "/object_models/" + this.objectModelId + "/entries/" + this.selectedObjectModelEntry.id + "/file_uploads";
    },
    //Where is save request being sent from
    fromUrl(){
      return window.location.href
    },
    entryRelationsToDestroy(): string[] {
      return this.$store.state.objectModelEntriesStore.entryRelationsToDestroy;
    },
    entryRelationIdsToDestroy(): string[] {
      return this.$store.state.objectModelEntriesStore.entryRelationsToDestroy.map(relation => relation.client_id);
    },
  },
  methods:{
    queueUploadForDeletion(upload): void {
      this.uploadsToDelete.push(upload);
    },
    pathForUpload(upload): string {
      return this.url + '/' + upload.id;
    },
    dropzoneFilesQueued(): boolean {
      const dropzones = this.getDropzones();
      return dropzones.some(dropzone => dropzone.getQueuedFiles().length > 0);
    },
    deleteUploads(): void {
      for (const upload of this.uploadsToDelete) {
        this.deleteUpload(upload)
      }
    },
    deleteUpload(upload: ListViewImageUpload): void {
      $.ajax({
        url: this.pathForUpload(upload),
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
          'Accept': 'application/json'
        },
      })
    },
    async createShellEntry(): Promise<void> {
      const [data] = await ObjectModelEntry.repo.provider.createShell({object_model_id: this.objectModelId});
      if (data.success) {
        this.selectedObjectModelEntry.id = data.entry.id;
      } else {
        Logger.errorOnce(
          'saveObjectModelEntry.createShellEntry',
          ['server error creating shell entry'] + data.errors
        )
      }

      return data.success
    },
    async save(): Promise<void> {
      this.selectedObjectModelEntry.validate();
      if (this.selectedObjectModelEntry.validator.isValid) {
        this.isSaving = true;
        this.showModal(this.vueModalID.savingRecord);
        this.modalStateFor(this.vueModalID.savingRecord).status = this.ModalStatus.InProgress;

        try {
          if (this.uploadsToDelete.length) this.deleteUploads();

          if (this.dropzoneFilesQueued()) {
            if (this.isEditingEntry) {
              await this.processDropzoneQueueAndSave();
            } else {
              const success = await this.createShellEntry()
              if (success) {
                await this.processDropzoneQueueAndSave();
              }
            }
          } else {
            await this.saveEntry();
          }
        } catch (err) {
          console.warn(err); // Preserve logs through navigation to see console warn
          this.isSaving = false;
          this.redirectToView("Action cannot be completed at this time.");
        }
      } else {
        // prevent entry save from failing silently in DV edit mode if a property BOTH is not editable from DV edit mode AND does not pass validation
        let errorsList: Array<{propName: string, propType: string, error: string}> = []
        this.selectedObjectModelEntry.validationErrors.forEach(err => {
          const propName = this.selectedObjectModelEntry.published_object_model.object_model_properties[err.field].label_title
          const propType = this.selectedObjectModelEntry.published_object_model.object_model_properties[err.field].displayName
          errorsList.push({propName, propType, error: err.message})
        })
        Logger.start('saveObjectModelEntryMixin#save', 'error')
        Logger.error('saveObjectModelEntryMixin#save', "Entry validation failed! See below for errors.")
        Logger.table('saveObjectModelEntryMixin#save', errorsList)
        Logger.stop('saveObjectModelEntryMixin#save')
      }
    },

    async processDropzoneQueueAndSave(): Promise<void> {
      const dropzones = this.getDropzones();
      const url = "/object_models/" + this.objectModelId + "/entries/" + this.selectedObjectModelEntry.id + "/file_uploads";

      for (const dropzone of dropzones) {
        dropzone.on("queuecomplete", (file) => {
          if (dropzone.getQueuedFiles().length === 0) {
            _.remove(dropzones, dropzone);
            if (dropzones.length === 0) {
              this.saveEntry();
            }
          }
        });

        dropzone.options.url = url;
        dropzone.options.autoProcessQueue = true;
        dropzone.processQueue();
      }
    },
    getDropzones(): any[] {
      const entry = this.selectedObjectModelEntry;
      const getDropzone = (property) => {
        try {
          return Dropzone.forElement(`#f_${property.client_id}-dropzone`)
        } catch (e) {
          Logger.warnOnce('Dropzone', e.message, 'Did your dropzone start disabled? If so, the dropzone was never initialized.')
          return null
        }
      }

      let fileUploadProperties
      // if there are no file uploads, then we need to grab all the Dropzones
      if (!entry.file_uploads) {
        fileUploadProperties = this.fileUploadProperties
      } else {
        // the dropzones for single file attachments disappear when they have a file uploaded
        // so we don't need to try to process them
        fileUploadProperties = this.filteredFileUploadProperties
      }
      // file upload properties aren't present when saving from a relation section
      return fileUploadProperties?.map(getDropzone).filter(dropzone => dropzone?.getQueuedFiles().length > 0) || [];
    },
    async saveEntry(): Promise<void> {
      let objJson = this.selectedObjectModelEntry.asJson();
      if (objJson["owner_email"]) {
        objJson["owner_email"] = objJson["owner_email"].toLowerCase();
      }
      objJson["inverse_entry_relations_attributes"] = this.generateInverseEntryRelationJson();
      objJson["entry_relations_attributes"] = this.generateEntryRelationJson();
      this.addAccountRelationsFromEmails();
      objJson["account_relations_attributes"] = this.generateAccountRelationsJson();

      let message: string = this.isEditingEntry
        ? `You have successfully edited the ${this.selectedObjectModel?.title ||  this.selectedObjectModelEntry?.published_object_model?.title }.`
        : `You have successfully created a new ${this.selectedObjectModel?.title || this.selectedObjectModelEntry?.published_object_model?.title}.`;

      const [data] = await ObjectModelEntry.repo.provider.save(this.selectedObjectModelEntry, objJson, this.fromUrl);

      const newEntryId = data?.['entry']?.['id'];

      if (data.success) {
        this.isSaving = false;
        if (data['jobs_in_queue']) {
          this.redirectToView(message, { job_ids: JSON.stringify(data['job_ids']) });
        } else {
          this.redirectToView(message, { entry_id: newEntryId });
        }
      } else {
        message = `Something went wrong while saving ${this.selectedObjectModel.title}. Please try again later.`;
        this.redirectToView(message);
      }
    },
    addAccountRelationsFromEmails(): void {
      const accountRelationships = this.selectedAccountRelationships;
      if (!accountRelationships) return;
      for (const accountRelationship of accountRelationships) {
        if (accountRelationship.hasMatchingEmails) {
          const accountRelationJson = {
            published_relation_id: accountRelationship.id,
            entry_id: this.selectedObjectModelEntry.id,
            user_email: accountRelationship.new_email,
            account_type: accountRelationship.accountType,
          }
          this.selectedObjectModelEntry.appendAccountRelation(accountRelationJson);
        }
      }
    },
    generateInverseEntryRelationJson(): object[] {
      return this.selectedObjectModelEntry.inverseEntryRelationsArray.map(inverseEntryRelation => {
        const shouldDestroy = this.entryRelationIdsToDestroy.includes(inverseEntryRelation.client_id);
        return {
          client_id: inverseEntryRelation.client_id,
          published_relation: inverseEntryRelation.published_relation_id,
          entry: inverseEntryRelation.entry_id,
          should_destroy: shouldDestroy,
        };
      });
    },
    generateEntryRelationJson(): object[] {
      return this.selectedObjectModelEntry.entryRelationsArray.map(entryRelation => {
        const shouldDestroy = this.entryRelationIdsToDestroy.includes(entryRelation.client_id);
        return {
          client_id: entryRelation.client_id,
          published_relation: entryRelation.published_relation_id,
          related_entry: entryRelation.related_entry_id,
          should_destroy: shouldDestroy,
        };
      });
    }
  }
}